/**
 * Pure paging state for the item list. Extracted from the component so the invariants
 * are unit-testable under plain Node — the interaction harness (Phase 1 step 9) will
 * cover the rendered half; this covers the half that can be wrong silently.
 */

import type { ItemCursor, ItemSummary } from '../../../api/src/catalog/items.js';

export interface PageState {
  rows: ItemSummary[];
  cursor: ItemCursor | null;
  /** True once a page came back with no nextCursor — the end is known, not assumed. */
  exhausted: boolean;
  /**
   * The count of all rows matching the current filters — the header's "N garments". Only
   * the FIRST page carries it (the API sends it once); `appendPage` holds it across the
   * scroll pages that follow. `null` until the first page lands.
   */
  total: number | null;
  /**
   * The unfiltered, non-retired tenant total — the "of M" in "N of M" (A1). Like `total`, only
   * the first page carries it and `appendPage` holds it. `null` until the first page lands.
   */
  grandTotal: number | null;
}

export const EMPTY_PAGE_STATE: PageState = { rows: [], cursor: null, exhausted: false, total: null, grandTotal: null };

/**
 * Append a page, deduplicating by id.
 *
 * WHY DEDUPE AT ALL, when keyset pagination promises no repeats: the promise holds for a
 * stable catalogue, and the catalogue is not stable — an item renamed between two page
 * fetches can legitimately reappear under its new sort position. React keys by id, and a
 * duplicated key makes rows vanish or double-render with no error in production builds.
 * The server guarantee and the client guard are answers to two different questions.
 */
export function appendPage(
  state: PageState,
  page: {
    items: ItemSummary[];
    nextCursor: ItemCursor | null;
    total?: number | undefined;
    grandTotal?: number | undefined;
  },
): PageState {
  const seen = new Set(state.rows.map((r) => r.id));
  const fresh = page.items.filter((r) => !seen.has(r.id));
  return {
    rows: [...state.rows, ...fresh],
    cursor: page.nextCursor,
    exhausted: page.nextCursor === null,
    // The first page carries `total`/`grandTotal`; later pages omit them, so hold what we have.
    total: page.total ?? state.total,
    grandTotal: page.grandTotal ?? state.grandTotal,
  };
}
